import { useRef, useState, useContext, useEffect } from "react";
import axios from "axios";
import axiosHttp from "../config/axios.interceptor";
import {
  base_url,
  dataURItoBlob,
  errorResponseMessage,
  httpHeaderConfig,
} from "./const.prop";
import { validateFile } from "../../utils/application-helper";
import { AppContext } from "../contexts/AppContext";
import { AuthContext } from "../contexts/AuthContext";
import Delete from "./Delete";
import { useSelector } from "react-redux";

const PostDocument = ({ data }) => {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);
  const inputRef = useRef();
  const [loadedFile, setLoadedFile] = useState(data ? data?.url : "");
  const [postText, setPostText] = useState(data ? data?.description : "");
  const [showButton, setShowButton] = useState(false);
  const [isNewFile, setNewFile] = useState(data?.url ? false : true);
  const [loading, setLoading] = useState(false);
  const [categoryId, setCategoryId] = useState(
    data?.skillCategoryId ? data?.skillCategoryId : ""
  );
  const [previewUrl, setPreviewUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const { categoryList } = useSelector((state) => state.utilData);
  let dataURL = null;

  const triggerInputClick = () => inputRef.current.click();
  const inputChangeHandler = (e) => {
    const file = inputRef.current.files[0];
    const validationRes = validateFile(file, "document");
    if (validationRes.errors.length) {
      appContext.updateNotificationState({
        type: "error",
        message: validationRes.errors[0],
        show: true,
      });
      return;
    }
    setSelectedFile(file);
    const blobUrl = URL.createObjectURL(file);
    setPreviewUrl(blobUrl);
    setLoadedFile(true);
    setNewFile(true);
  };

  useEffect(() => {
    return () => {
      if (previewUrl && !previewUrl.includes("linkaa-dev-pub")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const postSubmit = async () => {
    try {
      appContext.updateLoaderState(true);
      let uploadedUrl = null;

      // Upload file if there's a new one
      if (selectedFile && isNewFile) {
        const formData = new FormData();
        formData.append("image", selectedFile);

        const savedResponse = await axios.post(
          base_url + "upload-image",
          formData,
          {
            headers: {
              ...httpHeaderConfig,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (savedResponse.status !== 200) {
          if (savedResponse?.status >= 500) {
            appContext.updateNotificationState({
              show: true,
              message: "Something went wrong, please try again later.",
              type: "error",
            });
            return;
          }
          throw new Error(errorResponseMessage(savedResponse));
        }

        uploadedUrl = savedResponse.data.tempPath;
      }

      // Prepare form data
      const formValues = {
        type: "doc",
        text: postText,
        skillCategoryId: categoryId,
        ...(isNewFile && uploadedUrl && { url: uploadedUrl }),
      };

      // Submit post
      const endpoint = data?.id ? `post-data/${data.id}` : "post-data";
      const postResponse = await axiosHttp.post(endpoint, formValues);

      if (postResponse.status === 201) {
        appContext.updateLoaderState(false);
        appContext.updateModalState();
      }
    } catch (error) {
      if (error.response?.status === 429) {
        appContext.updateNotificationState({
          show: true,
          message: errorResponseMessage(error),
          type: "error",
        });
        authContext.logout();
        return;
      }
      if (error.response?.status >= 500) {
        appContext.updateNotificationState({
          show: true,
          message: "Something went wrong, please try again later.",
          type: "error",
        });
      } else {
        appContext.updateNotificationState({
          type: "error",
          message: errorResponseMessage(error),
          show: true,
        });
      }

      appContext.updateLoaderState(false);
      setShowButton(false);
    }
  };

  const reUploadHandler = () => {
    if (previewUrl && !previewUrl.includes("linkaa-dev-pub")) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setLoadedFile(null);
    setSelectedFile(null);
    inputRef.current.value = "";
    setNewFile(true);
  };

  const handleChange = (e) => {
    setPostText(e.target.value);
  };

  return (
    <>
      <div className="modal-body">
        <p className="p-2">
          <b>Note: </b>Only pdf or doc formats are supported
        </p>
        <div className="form-group category-select">
          <select
            className="form-control custom-select"
            onChange={(e) => setCategoryId(e.target.value)}
            disabled={
              data?.skillCategoryId === "680c89f84f3447f6da04284f"
                ? true
                : false
            }
            value={categoryId}
          >
            <option value="">Select category</option>
            {categoryList.map((item) => {
              return <option value={item?._id}>{item?.name}</option>;
            })}
          </select>
        </div>
        <div className="card card-modal">
          <div className="d-flex align-items-center justify-content-center height-inherit">
            {loading ? (
              <p>Uploading, please wait</p>
            ) : (
              !loadedFile && (
                <p
                  className="file text-center doc-txt cursor-pointer"
                  onClick={triggerInputClick}
                >
                  Select Document to upload
                  <br />
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="94"
                      height="94"
                      viewBox="0 0 94 94"
                      fill="none"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M54.7387 7.83447C54.9073 7.83447 55.0726 7.84869 55.2334 7.876L55.7626 7.87912C56.5616 7.87912 57.3253 8.20421 57.8815 8.77996L77.7233 29.4482C78.2442 29.9926 78.5383 30.725 78.5383 31.481V67.4751C78.6006 77.302 70.9396 85.2881 61.0971 85.6406H29.2154C19.6578 85.4352 11.9926 77.7119 11.7563 68.2516L11.7549 25.4219C11.986 15.7046 20.0073 7.87912 29.6501 7.87912L54.2441 7.876C54.4049 7.84869 54.5702 7.83447 54.7387 7.83447ZM51.7989 13.7526L29.6619 13.7541C23.172 13.7541 17.7826 19.0103 17.6299 25.4924V67.4751C17.4889 74.1374 22.7372 79.6207 29.3368 79.7656H60.9952C67.5321 79.5306 72.7061 74.1374 72.6632 67.4947L72.663 35.1846L64.7967 35.1885C57.6253 35.1689 51.8012 29.3252 51.8012 22.1656L51.7989 13.7526ZM43.642 35.8735C45.2635 35.8735 46.5795 37.1895 46.5795 38.811L46.578 55.3476L50.739 51.172C51.8827 50.0205 53.7431 50.0205 54.8946 51.1642C56.0422 52.3078 56.0461 54.1682 54.9025 55.3197L45.8196 64.4425C45.5893 64.6968 45.315 64.9107 45.0088 65.0721C44.9815 65.0805 44.9568 65.0931 44.9319 65.1053C44.8179 65.1671 44.6975 65.2167 44.5733 65.2582C44.5612 65.2567 44.5493 65.2606 44.5375 65.2644C44.2555 65.3602 43.9544 65.4091 43.642 65.4091C43.3384 65.4091 43.0454 65.3629 42.7698 65.2773C42.7265 65.2608 42.6804 65.2454 42.6346 65.2288C42.5491 65.2002 42.4671 65.1659 42.3871 65.128C42.3393 65.1034 42.2922 65.0796 42.2458 65.0546C42.1529 65.0061 42.0619 64.9502 41.9744 64.8897C41.9445 64.868 41.9148 64.8467 41.8856 64.8248C41.7595 64.7315 41.6414 64.6276 41.5323 64.5149L32.3738 55.3197C31.2301 54.1682 31.234 52.3078 32.3816 51.1642C33.5331 50.0205 35.3935 50.0205 36.5372 51.172L40.703 55.3554L40.7045 38.811C40.7045 37.1895 42.0205 35.8735 43.642 35.8735ZM57.6739 17.0465L57.6762 22.1656C57.6762 26.0979 60.8722 29.3017 64.8046 29.3135L69.4435 29.3096L57.6739 17.0465Z"
                        fill="#999999"
                        fillOpacity="0.6"
                      />
                    </svg>
                  </span>
                </p>
              )
            )}
          </div>
          <div>
            {loadedFile && (
              <div className="image-wrapper">
                <Delete clickHandler={reUploadHandler} />
                <iframe
                  src={data?.url && !isNewFile ? loadedFile : previewUrl}
                />
              </div>
            )}
          </div>

          <input
            type="text"
            className="form-control mt-3"
            value={postText}
            onChange={handleChange}
            placeholder="description..."
          />
        </div>
        <input
          className="visible-hidden"
          type="file"
          ref={inputRef}
          onChange={inputChangeHandler}
        />
      </div>
      <div className="modal-footer mt-2 mb-3">
        <button
          type="button"
          className="btn btn-outline-primary prime-btn"
          onClick={appContext.updateModalState}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-outline-primary modal-btn"
          // onClick={postHandler}
          onClick={postSubmit}
          disabled={!loadedFile || showButton || categoryId === ""}
        >
          Done
        </button>
      </div>
    </>
  );
};

export default PostDocument;
